import React, { useEffect, useRef, useState, createContext } from 'react';
import api, { cooperplaceApi } from 'services/api';
import { isAuthenticated } from 'services/auth';

// Intervalo mínimo entre recargas das permissões quando a aba volta ao foco.
const PERMISSIONS_FOCUS_INTERVAL = 60 * 1000;

export const UserContext = createContext({
  isUserActive: false,
  isLoading: true,
  permissionsVersion: 0,
  refreshPermissions: async () => {},
});

export function UserContextProvider({ children }) {
  const [checkUser, setCheckUser] = useState(false);
  const [userBlocked, setUserBlocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [shouldUserChangePassword, setShouldUserChangePassword] = useState(false);
  const [lastPasswordUpdate, setLastPasswordUpdate] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState({});
  const [enabledToSelect, setEnabledToSelect] = useState(false);
  const [changedCompany, setChangedCompany] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isCooperMail, setIsCooperMail] = useState(false);
  const [permissionsVersion, setPermissionsVersion] = useState(0);
  const permissionsFetchedAt = useRef(0);

  // Permissões efetivas do usuário logado (perfil principal, perfis adicionais e permissões
  // adicionais), lidas por validatePermission a partir do localStorage.
  async function refreshPermissions() {
    const { data } = await cooperplaceApi.get('users/permissions');
    localStorage.setItem('permissions', JSON.stringify(data));
    permissionsFetchedAt.current = Date.now();
    setPermissionsVersion((version) => version + 1);
  }

  async function checkUserStatus() {
    try {
      setLoading(true);
      const response = await api.get('v3/user/active');

      // flags principais (com default seguro)
      setCheckUser(Boolean(response?.data?.isUserActive));
      setUserBlocked(Boolean(response?.data?.isUserBlocked));

      // extrai user aceitando array ou objeto
      const userData = response?.data?.user;
      const user = Array.isArray(userData) ? userData?.[0] : (userData ?? null);

      await refreshPermissions();

      setCurrentUser(user);
      const isCooperMail = user?.email?.includes('@coopercarga.com.br');
      setIsCooperMail(isCooperMail);
      const hasShouldChangePasswordInfo =
        user?.change_password === true || user?.change_password === false
          ? user?.change_password
          : true;
      setShouldUserChangePassword(hasShouldChangePasswordInfo);
      setLastPasswordUpdate(user?.last_update_password);

      if (user) {
        delete user.permissions;
        delete user.change_password;
        delete user.last_update_password;
      }

      // limpa campos grandes antes de persistir o usuario no localStorage
      if (user) {
        const { permissions: _p, change_password: _cp, last_update_password: _lup, ...rest } = user;
        setCurrentUser(user);
        localStorage.setItem('usuario', JSON.stringify(rest));
      } else {
        setCurrentUser(null);
        localStorage.removeItem('usuario');
      }
    } catch (error) {
      console.error('checkUserStatus error:', error);
      // em caso de erro, deixe estados num padrão seguro
      setCheckUser(false);
      setUserBlocked(false);
      setCurrentUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function getCompaniesUser() {
    try {
      const { data } = await cooperplaceApi.get('user/companies');
      setCompanies(Array.isArray(data?.companies) ? data.companies : []);
      setSelectedCompany(data?.currentCompany ?? {});
    } catch (error) {
      console.error('getCompaniesUser error:', error);
      setCompanies([]);
    }
  }

  // Carrega ao montar; roda novamente se o company.id mudar
  useEffect(() => {
    checkUserStatus();
    getCompaniesUser();
  }, [selectedCompany?.id]);

  // Alterações feitas por um administrador chegam sem novo login quando a aba volta ao foco.
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState !== 'visible' || !isAuthenticated()) return;
      if (Date.now() - permissionsFetchedAt.current < PERMISSIONS_FOCUS_INTERVAL) return;
      refreshPermissions().catch((error) => console.error('refreshPermissions error:', error));
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  return (
    <UserContext.Provider
      value={{
        isLoading: loading,
        isUserActive: checkUser,
        isUserBlocked: userBlocked,
        shouldUserChangePassword,
        lastPasswordUpdate,
        enabledToSelect,
        setEnabledToSelect,
        companies,
        selectedCompany,
        setSelectedCompany,
        changedCompany,
        setChangedCompany,
        currentUser,
        getCompaniesUser,
        isCooperMail,
        permissionsVersion,
        refreshPermissions,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}
